import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, Truck, Users as UsersIcon, Wallet } from 'lucide-react';
import apiService from '../../services/api';
import { formatARS } from '../../utils/formatCurrency';

interface AccountRow { partyId: number; name: string; company: string | null; balance: number; }
type PartyType = 'supplier' | 'client';

const CuentasCorrientes: React.FC = () => {
  const navigate = useNavigate();
  const [partyType, setPartyType] = useState<PartyType>('supplier');
  const [items, setItems] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const load = async (type: PartyType) => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getAccountsSummary(type);
      setItems(data.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo cargar la cuenta corriente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(partyType); }, [partyType]);

  const filtered = items.filter(item => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return item.name.toLowerCase().includes(term) || (item.company || '').toLowerCase().includes(term);
  });

  const totalBalance = items.reduce((sum, item) => sum + item.balance, 0);
  const balanceLabel = partyType === 'supplier' ? 'Les debo' : 'Me deben';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cuentas corrientes</h1>
          <p className="text-gray-600">Saldo con proveedores y clientes: documentos recibidos/emitidos y pagos (cheques, efectivo, transferencias).</p>
        </div>
        <button onClick={() => load(partyType)} className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm self-start"><RefreshCw className="h-4 w-4" />Actualizar</button>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        <button onClick={() => setPartyType('supplier')} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 ${partyType === 'supplier' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}><Truck className="h-4 w-4" />Proveedores</button>
        <button onClick={() => setPartyType('client')} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 ${partyType === 'client' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}><UsersIcon className="h-4 w-4" />Clientes</button>
      </div>

      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
        <Wallet className="h-8 w-8 text-primary-600" />
        <div><p className="text-sm text-gray-500">{balanceLabel} en total</p><p className="text-xl font-bold">{formatARS(totalBalance)}</p></div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar..." className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="divide-y md:hidden">
          {loading ? <div className="px-5 py-6 text-center text-gray-500">Cargando...</div> :
            filtered.map(item => (
              <button key={item.partyId} onClick={() => navigate(`/admin/cuentas-corrientes/${partyType}/${item.partyId}`)} className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">{item.name}</p>
                  {partyType === 'client' && item.company && <p className="text-sm text-gray-500">{item.company}</p>}
                </div>
                <span className={`font-semibold ${item.balance > 0 ? 'text-red-600' : item.balance < 0 ? 'text-green-700' : 'text-gray-500'}`}>{formatARS(item.balance)}</span>
              </button>
            ))}
          {!loading && !filtered.length && <div className="px-5 py-10 text-center text-gray-500">No se encontraron resultados.</div>}
        </div>
        <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr><th className="px-5 py-3">Nombre</th>{partyType === 'client' && <th className="px-5 py-3">Empresa</th>}<th className="px-5 py-3">Saldo</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={4} className="px-5 py-6 text-center text-gray-500">Cargando...</td></tr> :
              filtered.map(item => (
                <tr key={item.partyId} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/admin/cuentas-corrientes/${partyType}/${item.partyId}`)}>
                  <td className="px-5 py-3 font-medium">{item.name}</td>
                  {partyType === 'client' && <td className="px-5 py-3">{item.company || '-'}</td>}
                  <td className={`px-5 py-3 font-semibold ${item.balance > 0 ? 'text-red-600' : item.balance < 0 ? 'text-green-700' : 'text-gray-500'}`}>{formatARS(item.balance)}</td>
                  <td className="px-5 py-3 text-right text-sm text-primary-600">Ver cuenta →</td>
                </tr>
              ))}
            {!loading && !filtered.length && <tr><td colSpan={4} className="px-5 py-10 text-center text-gray-500">No se encontraron resultados.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default CuentasCorrientes;
